import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  Image,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  Platform,
} from 'react-native';
import { imageService } from '../services/images';
import { Colors, Fonts, Radii, Shadows, Spacing } from '../constants';

interface ImageGalleryProps {
  publicIds: string[];
  onImagePress?: (index: number) => void;
  /** Mobile detail page: edge-to-edge square photo (1:1), no radius or side padding */
  fullBleed?: boolean;
  /**
   * Desktop detail page: one 4:3 photo with a "1 / 3" counter tag and a row of
   * 80×60 thumbnails under it; tapping a thumbnail switches the main photo.
   */
  thumbnails?: boolean;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({
  publicIds,
  onImagePress,
  fullBleed = false,
  thumbnails = false,
}) => {
  const { width: screenWidth } = useWindowDimensions();
  const [activeIndex, setActiveIndex] = useState(0);

  // A different set of photos (another item, or an edit) starts at the first
  // one again. Keyed on the ids so a re-created array alone doesn't reset it.
  const publicIdsKey = (publicIds || []).join('|');
  useEffect(() => {
    setActiveIndex(0);
  }, [publicIdsKey]);

  if (!publicIds || publicIds.length === 0) {
    return null;
  }

  if (thumbnails) {
    const active = Math.min(activeIndex, publicIds.length - 1);
    return (
      <View>
        <TouchableOpacity
          onPress={() => onImagePress?.(active)}
          disabled={!onImagePress}
          activeOpacity={0.9}
        >
          <Image
            source={{ uri: imageService.getFullSizeUrl(publicIds[active]) }}
            style={styles.mainImage}
            resizeMode="cover"
          />
        </TouchableOpacity>
        {publicIds.length > 1 ? (
          <>
            <View style={styles.counter} pointerEvents="none">
              <Text style={styles.counterText}>{`${active + 1} / ${publicIds.length}`}</Text>
            </View>
            <View style={styles.thumbRow}>
              {publicIds.map((publicId, index) => (
                <TouchableOpacity
                  key={`${publicId}-${index}`}
                  onPress={() => setActiveIndex(index)}
                  activeOpacity={0.8}
                  accessibilityRole="button"
                >
                  <Image
                    source={{ uri: imageService.getThumbnailUrl(publicId) }}
                    style={[styles.thumb, index === active && styles.thumbActive]}
                    resizeMode="cover"
                  />
                </TouchableOpacity>
              ))}
            </View>
          </>
        ) : null}
      </View>
    );
  }

  // Calculate image dimensions based on count
  // For multiple images, show them side-by-side
  const getImageDimensions = () => {
    if (fullBleed) {
      // One square photo per page; swipe for the next one
      return { width: screenWidth, height: screenWidth };
    }
    const maxWebWidth = 768; // Matches ItemDetailsScreen maxWidth of 800px - padding
    const containerWidth = Platform.OS === 'web'
      ? Math.min(screenWidth - 32, maxWebWidth)
      : screenWidth - 32;

    if (publicIds.length === 1) {
      // Single image: use full width
      const width = containerWidth;
      return { width, height: width * 0.75 };
    } else {
      // Multiple images: split width to show both
      const gap = 12;
      const width = (containerWidth - gap) / 2; // Half width minus gap
      return { width, height: width * 0.75 };
    }
  };

  const { width: imageWidth, height: imageHeight } = getImageDimensions();

  const renderItem = ({ item, index }: { item: string; index: number }) => {
    const imageUrl = imageService.getFullSizeUrl(item);

    return (
      <TouchableOpacity
        onPress={() => onImagePress?.(index)}
        disabled={!onImagePress}
        activeOpacity={0.8}
        style={Platform.OS === 'web' ? ({ touchAction: 'pan-y' } as any) : undefined}
      >
        <Image
          source={{ uri: imageUrl }}
          style={[fullBleed ? styles.imageFullBleed : styles.image, { width: imageWidth, height: imageHeight }]}
          resizeMode="cover"
        />
      </TouchableOpacity>
    );
  };

  return (
    <View style={fullBleed ? styles.containerFullBleed : styles.container}>
      <FlatList
        data={publicIds}
        renderItem={renderItem}
        keyExtractor={(item, index) => `${item}-${index}`}
        horizontal
        pagingEnabled={fullBleed}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={fullBleed ? undefined : styles.contentContainer}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: Spacing.base,
  },
  containerFullBleed: {
    backgroundColor: Colors.card,
  },
  contentContainer: {
    paddingHorizontal: Spacing.base,
    gap: Spacing.md,
  },
  image: {
    borderRadius: Radii.card,
    backgroundColor: Colors.card,
    ...Shadows.card,
  },
  imageFullBleed: {
    borderRadius: 0,
    backgroundColor: Colors.card,
  },
  mainImage: {
    width: '100%',
    aspectRatio: 4 / 3,
    borderRadius: Radii.card,
    backgroundColor: Colors.card,
  },
  counter: {
    position: 'absolute',
    top: Spacing.md,
    left: Spacing.md,
    paddingHorizontal: Spacing.sm,
    paddingVertical: 3,
    borderRadius: Radii.tag,
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.borderLight,
  },
  counterText: {
    fontFamily: Fonts.semiBold,
    fontSize: 11,
    lineHeight: 14,
    color: Colors.textLight,
  },
  thumbRow: {
    flexDirection: 'row',
    // Wrap instead of squashing the 80×60 thumbnails when there are many.
    flexWrap: 'wrap',
    gap: Spacing.sm,
    rowGap: Spacing.sm,
    marginTop: Spacing.sm,
  },
  thumb: {
    width: 80,
    height: 60,
    borderRadius: Radii.thumb,
    backgroundColor: Colors.card,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  thumbActive: {
    borderColor: Colors.primary,
  },
});
